import {
  Divider,
  List,
  ListItem,
  ListSubheader,
  Paper,
  Typography,
  styled,
} from '@mui/material';
import preventDefault from '@segment/prevent-default';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import {KEY_DOWN, KEY_RETURN, KEY_UP} from 'keycode-js';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React, {Component, Fragment} from 'react';
import {connect} from 'react-redux';
import {
  geocode as search,
  reset as resetGeocoder,
} from '../actions/geocoder.js';
import {setExtent as setMapExtent} from '../actions/map.js';
import QueryInput from './query-input.jsx';

const Wrapper = styled(Paper)({
  width: 360,
  position: 'absolute',
  top: 12,
  left: 12,
});

const ListItemText = props => (
  <Typography noWrap color="inherit" variant="body1" {...props} />
);

class SearchInput extends Component {
  static propTypes = {
    error: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    results: PropTypes.array.isRequired,
  };

  state = {
    focused: false,
    selectedIndex: 0,
    query: '',
  };

  componentDidMount() {
    this.debouncedSearch = debounce(this.search, 250, {
      leading: true,
      trailing: true,
    });
  }

  componentWillUnmount() {
    this.debouncedSearch.cancel();
  }

  onKeyDown = event => {
    const isUpPressed = event.keyCode === KEY_UP;
    if (isUpPressed || event.keyCode === KEY_DOWN) {
      event.preventDefault();

      const direction = isUpPressed ? -1 : 1;
      this.setState(prevState => ({
        selectedIndex: Math.min(
          this.props.results.length - 1,
          Math.max(0, prevState.selectedIndex + direction),
        ),
      }));
    } else if (event.keyCode === KEY_RETURN) {
      const result = this.props.results[this.state.selectedIndex];
      if (result) {
        this.selectResult(result, this.state.selectedIndex);
      }
    }
  };

  onChange = query => {
    this.setState({query});

    if (!query) {
      this.debouncedSearch.cancel();
      this.props.dispatch(resetGeocoder());
      return;
    }

    this.debouncedSearch(query);
  };

  onFocus = () => this.setState({focused: true});

  onBlur = () => this.setState({focused: false});

  selectResult = (result, index) => {
    this.input.blur();
    this.props.dispatch(setMapExtent(result.bbox));
    this.setState({
      query: result.text,
      selectedIndex: index,
    });
  };

  search = query => this.props.dispatch(search(query));

  renderEmptyState() {
    if (this.props.error) {
      return `${this.props.error}`;
    }
    if (this.props.loading) {
      return 'Loading...';
    }
    return 'No results found';
  }

  renderResults() {
    if (this.props.results.length === 0) {
      return (
        <Fragment>
          <Divider />
          <ListItem>
            <ListItemText>{this.renderEmptyState()}</ListItemText>
          </ListItem>
        </Fragment>
      );
    }

    return this.props.results.map((result, index) => {
      const matches = match(result.place_name, this.state.query);
      const parts = parse(result.place_name, matches);

      return (
        <Fragment key={result.id}>
          <Divider />
          <ListItem
            data-qe="search-input-suggestion"
            button
            onClick={() => this.selectResult(result, index)}
            onMouseDown={preventDefault}
            selected={index === this.state.selectedIndex}
          >
            <ListItemText title={result.place_name}>
              {parts.map((part, index) => (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  style={{
                    fontWeight: part.highlight ? 700 : 400,
                  }}
                >
                  {part.text}
                </span>
              ))}
            </ListItemText>
          </ListItem>
        </Fragment>
      );
    });
  }

  render() {
    return (
      <Wrapper>
        <List
          disablePadding
          data-qe="search-location-box"
          subheader={
            <ListSubheader
              component="div"
              disableGutters
              style={{display: 'flex'}}
            >
              <QueryInput
                data-qe="input-search-location"
                inputRef={node => {
                  this.input = node;
                }}
                onBlur={this.onBlur}
                onChange={this.onChange}
                onFocus={this.onFocus}
                onKeyDown={this.onKeyDown}
                placeholder="Search for a location"
                value={this.state.query}
              />
            </ListSubheader>
          }
        >
          <div
            onMouseEnter={this.onResultsMouseEnter}
            data-qe="search-input-suggestions"
          >
            {this.state.focused &&
              this.state.query &&
              (this.props.results || this.props.loading) &&
              this.renderResults()}
          </div>
        </List>
      </Wrapper>
    );
  }
}

const mapStateToProps = state => ({
  error: state.geocoder.error,
  loading: !!state.geocoder.loading,
  results: state.geocoder.items,
});

export default connect(mapStateToProps)(SearchInput);
